import React from "react";

/**
 * A row of tiles that scrolls sideways: the one engine for any section that
 * shows several items at once and lets a visitor swipe through the rest.
 *
 * The scrolling is CSS (scroll-snap), so a phone swipes through it before any
 * JavaScript loads, and with none at all. The arrow buttons
 * (components/ui/scroll-strip-arrows) are the only part that needs JavaScript;
 * they page by about one screen of tiles.
 */

/** Whether there is more of the row to either side. */
interface ScrollEdges {
  back: boolean;
  forward: boolean;
}

/** The scroll state the arrows read, and the ref the track takes. */
interface ScrollStrip {
  trackRef: React.RefObject<HTMLDivElement | null>;
  canScrollBack: boolean;
  canScrollForward: boolean;
  scrollByPage: (direction: -1 | 1) => void;
}

const
  /** How much of the visible width one arrow press moves, so a sliver of the last tile stays in view. */
  PAGE_FRACTION = 0.85,
  /** Sub-pixel scroll positions count as the edge. */
  EDGE_TOLERANCE_PX = 1,

  /**
   * The track's wrapper. Its width is the page column, read as cqw below: scroll
   * padding takes a percentage of the track, which at full bleed is the screen.
   */
  SCROLL_STRIP_FRAME_CLASS = "jobsite:@container",

  /**
   * The track's classes: a snapping row that bleeds to the screen edge at every
   * width, its padding keeping the first tile on the page's left edge, so the next
   * tile peeks in as the cue to swipe. Its section clips the overflow (100vw counts
   * a desktop scrollbar). Scrollbar hidden; the arrows and the peek do its job.
   */
  SCROLL_STRIP_TRACK_CLASS =
    "jobsite:ml-[calc(50cqw-50vw)] jobsite:flex jobsite:w-screen jobsite:max-w-none jobsite:snap-x jobsite:snap-mandatory jobsite:gap-3 jobsite:overflow-x-auto jobsite:overscroll-x-contain jobsite:scroll-px-[calc(50vw-50cqw)] jobsite:px-[calc(50vw-50cqw)] jobsite:pb-1 jobsite:[scrollbar-width:none] jobsite:sm:gap-4 jobsite:[&::-webkit-scrollbar]:hidden",

  /** The row over the strip: heading then arrows stacked on a phone, side by side from a tablet up. */
  SCROLL_STRIP_HEADER_CLASS =
    "jobsite:mb-8 jobsite:flex jobsite:flex-col jobsite:items-start jobsite:gap-4 jobsite:sm:flex-row jobsite:sm:items-end jobsite:sm:justify-between jobsite:sm:gap-6 jobsite:md:mb-10",

  /** One tile's classes: most of a phone's width, under half a tablet's, under a third of a desktop's, so one always peeks. */
  SCROLL_STRIP_ITEM_CLASS =
    "jobsite:shrink-0 jobsite:snap-start jobsite:basis-[82%] jobsite:sm:basis-[44%] jobsite:lg:basis-[29%]",

  /**
   * Tells `onChange` whether a track has more to either side, now and whenever it scrolls or resizes.
   * @param {HTMLDivElement} track The scrolling row.
   * @param {(edges: ScrollEdges) => void} onChange Gets each new state.
   * @returns {() => void} Stops watching.
   */
  watchScrollEdges = (track: HTMLDivElement, onChange: (edges: ScrollEdges) => void): (() => void) => {
    const update = (): void => {
      const furthest = track.scrollWidth - track.clientWidth;
      onChange({
        back: track.scrollLeft > EDGE_TOLERANCE_PX,
        forward: track.scrollLeft < furthest - EDGE_TOLERANCE_PX,
      });
    };
    update();
    track.addEventListener("scroll", update, { passive: true });
    // Tiles and photos change the width after mount; jsdom and old browsers have no observer.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(track);
    return () => {
      track.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  },

  /**
   * The scroll state of one strip: whether there is more to either side, and
   * a way to page. Server-rendered as "more to the right", the usual case.
   * @returns {ScrollStrip} The track ref and the arrows' state.
   */
  useScrollStrip = (): ScrollStrip => {
    const trackRef = React.useRef<HTMLDivElement | null>(null),
      [edges, setEdges] = React.useState<ScrollEdges>({ back: false, forward: true });

    React.useEffect(() => {
      const track = trackRef.current;
      return track ? watchScrollEdges(track, setEdges) : undefined;
    }, []);

    const scrollByPage = React.useCallback((direction: -1 | 1): void => {
      const track = trackRef.current;
      if (!track) {
        return;
      }
      const reduceMotion =
        typeof globalThis.matchMedia === "function" && globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
      track.scrollBy({ behavior: reduceMotion ? "auto" : "smooth", left: direction * track.clientWidth * PAGE_FRACTION });
    }, []);

    return { canScrollBack: edges.back, canScrollForward: edges.forward, scrollByPage, trackRef };
  };

export type { ScrollStrip };
export { SCROLL_STRIP_FRAME_CLASS, SCROLL_STRIP_HEADER_CLASS, SCROLL_STRIP_ITEM_CLASS, SCROLL_STRIP_TRACK_CLASS, useScrollStrip };
